import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Contact from '../../../models/Contact';
import { markdownToTiptapJson, tiptapToMarkdown } from '../veraPromptEditorUtils';
import {
  clearAdminAssistDraftStorage,
  saveAdminAssistCoachingSandbox,
  saveAdminAssistIntentSandbox,
  scheduleAdminAssistDraftSave,
  syncAdminAssistPreviewGrounding,
  syncAdminAssistPreviewIntent,
  syncAdminAssistPreviewPrompt,
} from './adminAssistSandboxApi';
import { loadInitialAdminAssistPrompts } from './adminAssistSandboxLoaders';
import { useAdminAssistSandboxWorkflow } from './useAdminAssistSandboxWorkflow';
import {
  AdminAssistDraft,
  DRAFT_KEY,
  PreviewAssistTranscriptMessage,
  VeraAdminChatAssistPromptSandboxState,
  adminAssistDraftHasUnsavedChanges,
  draftTextEqual,
  parseAdminAssistDraft,
} from './veraAdminChatAssistPromptSandboxUtils';

export type { VeraAdminChatAssistPromptSandboxState };

export function useVeraAdminChatAssistPromptSandbox(): VeraAdminChatAssistPromptSandboxState {
  const sessionIdRef = useRef(uuidv4());
  const [promptTab, setPromptTab] = useState(0);
  const [rawMode, setRawMode] = useState(false);

  const [markdown, setMarkdown] = useState('');
  const [productionMarkdown, setProductionMarkdown] = useState('');
  const [promptSynced, setPromptSynced] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const lastSubmittedRef = useRef('');
  const savePromptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingInitRef = useRef<string | null>(null);
  const suppressEditorSyncRef = useRef(true);
  const userHasEditedRef = useRef(false);
  const saveDraftDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productionBaselinesRef = useRef({ markdown: '', intent: '', grounding: '' });
  const setEditorContentRef = useRef<(value: string) => void>(() => undefined);
  const clearAssistResultRef = useRef<() => void>(() => undefined);

  const [intentText, setIntentText] = useState('');
  const intentTextRef = useRef('');
  const [intentProductionText, setIntentProductionText] = useState('');
  const [intentSynced, setIntentSynced] = useState(true);
  const [intentIsDirty, setIntentIsDirty] = useState(false);
  const intentLastSubmittedRef = useRef<string | null>(null);
  const saveIntentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [groundingText, setGroundingText] = useState('');
  const groundingTextRef = useRef('');
  const [groundingProductionText, setGroundingProductionText] = useState('');
  const [groundingSynced, setGroundingSynced] = useState(false);
  const [groundingIsDirty, setGroundingIsDirty] = useState(false);
  const groundingLastSubmittedRef = useRef<string | null>(null);
  const saveGroundingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [savedDraft, setSavedDraft] = useState<AdminAssistDraft | null>(null);

  const scheduleDraftSave = useCallback(
    (
      nextMarkdown: string,
      nextIntent: string,
      nextGrounding: string,
      contact: Contact | null,
      messages: PreviewAssistTranscriptMessage[],
    ) => {
      scheduleAdminAssistDraftSave(
        userHasEditedRef.current,
        nextMarkdown,
        nextIntent,
        nextGrounding,
        contact,
        messages,
        saveDraftDebounceRef,
      );
    },
    [],
  );

  const syncPromptNow = useCallback((value: string) => {
    setPromptSynced(false);
    syncAdminAssistPreviewPrompt(sessionIdRef.current, value)
      .then(() => setPromptSynced(true))
      .catch(() => setPromptSynced(false));
  }, []);

  const syncIntentNow = useCallback((value: string) => {
    setIntentSynced(false);
    syncAdminAssistPreviewIntent(sessionIdRef.current, value)
      .then(() => setIntentSynced(true))
      .catch(() => setIntentSynced(false));
  }, []);

  const syncGroundingNow = useCallback((value: string) => {
    setGroundingSynced(false);
    syncAdminAssistPreviewGrounding(sessionIdRef.current, value)
      .then(() => setGroundingSynced(true))
      .catch(() => setGroundingSynced(false));
  }, []);

  const workflow = useAdminAssistSandboxWorkflow({
    sessionIdRef,
    prompts: {
      markdown,
      productionMarkdown,
      intentText,
      intentProductionText,
      groundingText,
      groundingProductionText,
      intentTextRef,
      groundingTextRef,
      lastSubmittedRef,
      intentLastSubmittedRef,
      groundingLastSubmittedRef,
      userHasEditedRef,
      setMarkdown,
      setEditorContent: (value) => setEditorContentRef.current(value),
      setIsDirty,
      setIntentText,
      setIntentIsDirty,
      setGroundingText,
      setGroundingIsDirty,
      setPromptSynced,
      setIntentSynced,
      setGroundingSynced,
      setSavedDraft,
      syncPromptNow,
      syncIntentNow,
      syncGroundingNow,
      scheduleDraftSave,
      clearAssistResult: () => clearAssistResultRef.current(),
    },
  });

  clearAssistResultRef.current = workflow.clearAssistResult;

  const editor = useEditor({
    extensions: [StarterKit],
    content: { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate: ({ editor: ed }) => {
      if (suppressEditorSyncRef.current) return;
      userHasEditedRef.current = true;
      const md = tiptapToMarkdown(ed.getJSON());
      setMarkdown(md);
      workflow.clearAssistResult();
      if (lastSubmittedRef.current) setIsDirty(md.trim() !== lastSubmittedRef.current.trim());
      scheduleDraftSave(
        md,
        intentTextRef.current,
        groundingTextRef.current,
        workflow.selectedContact,
        workflow.transcript,
      );
      setPromptSynced(false);
      if (savePromptTimerRef.current) clearTimeout(savePromptTimerRef.current);
      savePromptTimerRef.current = setTimeout(() => {
        syncPromptNow(md);
        saveAdminAssistCoachingSandbox(md);
      }, 1200);
    },
  });

  const setEditorContent = useCallback(
    (value: string) => {
      if (!editor) {
        pendingInitRef.current = value;
        return;
      }
      suppressEditorSyncRef.current = true;
      editor.commands.setContent(markdownToTiptapJson(value) as any);
      requestAnimationFrame(() => {
        suppressEditorSyncRef.current = false;
      });
    },
    [editor],
  );

  setEditorContentRef.current = setEditorContent;

  useEffect(() => {
    if (!editor || pendingInitRef.current === null) return;
    setEditorContent(pendingInitRef.current);
    pendingInitRef.current = null;
  }, [editor, setEditorContent]);

  useEffect(() => {
    const draft = parseAdminAssistDraft(localStorage.getItem(DRAFT_KEY));
    loadInitialAdminAssistPrompts(draft)
      .then((loaded) => {
        setProductionMarkdown(loaded.productionMarkdown);
        lastSubmittedRef.current = loaded.productionMarkdown;
        productionBaselinesRef.current.markdown = loaded.productionMarkdown;
        setMarkdown(loaded.coachingMarkdown);
        setIsDirty(!draftTextEqual(loaded.coachingMarkdown, loaded.productionMarkdown));
        setEditorContent(loaded.coachingMarkdown);
        if (loaded.transcript.length) workflow.setTranscript(loaded.transcript);

        setIntentProductionText(loaded.intentProductionText);
        intentLastSubmittedRef.current = loaded.intentProductionText;
        productionBaselinesRef.current.intent = loaded.intentProductionText;
        intentTextRef.current = loaded.intentText;
        setIntentText(loaded.intentText);
        setIntentIsDirty(
          draft?.intentText !== undefined &&
            !draftTextEqual(draft.intentText, loaded.intentProductionText),
        );

        setGroundingProductionText(loaded.groundingProductionText);
        groundingLastSubmittedRef.current = loaded.groundingProductionText;
        productionBaselinesRef.current.grounding = loaded.groundingProductionText;
        groundingTextRef.current = loaded.groundingText;
        setGroundingText(loaded.groundingText);
        setGroundingIsDirty(
          draft?.groundingText !== undefined &&
            !draftTextEqual(draft.groundingText, loaded.groundingProductionText),
        );

        if (draft && adminAssistDraftHasUnsavedChanges(draft, productionBaselinesRef.current)) {
          setSavedDraft(draft);
        } else {
          clearAdminAssistDraftStorage();
          setSavedDraft(null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRawMarkdownChange = (value: string) => {
    userHasEditedRef.current = true;
    setMarkdown(value);
    workflow.clearAssistResult();
    if (lastSubmittedRef.current) setIsDirty(value.trim() !== lastSubmittedRef.current.trim());
    scheduleDraftSave(
      value,
      intentTextRef.current,
      groundingTextRef.current,
      workflow.selectedContact,
      workflow.transcript,
    );
    setPromptSynced(false);
    if (savePromptTimerRef.current) clearTimeout(savePromptTimerRef.current);
    savePromptTimerRef.current = setTimeout(() => {
      syncPromptNow(value);
      saveAdminAssistCoachingSandbox(value);
    }, 1200);
  };

  const handleIntentChange = (value: string) => {
    userHasEditedRef.current = true;
    intentTextRef.current = value;
    setIntentText(value);
    workflow.clearAssistResult();
    if (intentLastSubmittedRef.current !== null) {
      setIntentIsDirty(value.trim() !== intentLastSubmittedRef.current.trim());
    }
    scheduleDraftSave(
      markdown,
      value,
      groundingTextRef.current,
      workflow.selectedContact,
      workflow.transcript,
    );
    setIntentSynced(false);
    if (saveIntentTimerRef.current) clearTimeout(saveIntentTimerRef.current);
    saveIntentTimerRef.current = setTimeout(() => {
      syncIntentNow(value);
      saveAdminAssistIntentSandbox(value);
    }, 1200);
  };

  const resetIntentToProduction = () => {
    intentTextRef.current = intentProductionText;
    setIntentText(intentProductionText);
    setIntentIsDirty(false);
    syncIntentNow(intentProductionText);
    saveAdminAssistIntentSandbox(intentProductionText);
  };

  const handleGroundingChange = (value: string) => {
    userHasEditedRef.current = true;
    groundingTextRef.current = value;
    setGroundingText(value);
    workflow.clearAssistResult();
    if (groundingLastSubmittedRef.current !== null) {
      setGroundingIsDirty(value.trim() !== groundingLastSubmittedRef.current.trim());
    }
    scheduleDraftSave(
      markdown,
      intentTextRef.current,
      value,
      workflow.selectedContact,
      workflow.transcript,
    );
    setGroundingSynced(false);
    if (saveGroundingTimerRef.current) clearTimeout(saveGroundingTimerRef.current);
    saveGroundingTimerRef.current = setTimeout(() => syncGroundingNow(value), 1200);
  };

  const resetGroundingToProduction = () => {
    groundingTextRef.current = groundingProductionText;
    setGroundingText(groundingProductionText);
    setGroundingIsDirty(false);
    syncGroundingNow(groundingProductionText);
  };

  const syncEditorFromMarkdown = () => {
    editor?.commands.setContent(markdownToTiptapJson(markdown) as any);
  };

  return {
    sessionId: sessionIdRef.current,
    promptTab,
    setPromptTab,
    rawMode,
    setRawMode,
    markdown,
    productionMarkdown,
    promptSynced,
    isDirty,
    intentText,
    intentProductionText,
    intentSynced,
    intentIsDirty,
    groundingText,
    groundingProductionText,
    groundingSynced,
    groundingIsDirty,
    contactSearch: workflow.contactSearch,
    setContactSearch: workflow.setContactSearch,
    contactOptions: workflow.contactOptions,
    contactLoading: workflow.contactLoading,
    selectedContact: workflow.selectedContact,
    setSelectedContact: workflow.setSelectedContact,
    transcript: workflow.transcript,
    investorComposeText: workflow.investorComposeText,
    setInvestorComposeText: workflow.setInvestorComposeText,
    adminComposeText: workflow.adminComposeText,
    setAdminComposeText: workflow.setAdminComposeText,
    assistLoading: workflow.assistLoading,
    assistResult: workflow.assistResult,
    assistError: workflow.assistError,
    saving: workflow.saving,
    error: workflow.error,
    submittedProposal: workflow.submittedProposal,
    savedDraft,
    editor,
    hasInvestorMessage: workflow.hasInvestorMessage,
    handleRawMarkdownChange,
    handleIntentChange,
    resetIntentToProduction,
    handleGroundingChange,
    resetGroundingToProduction,
    dismissDraft: workflow.dismissDraft,
    addMessage: workflow.addMessage,
    resetSandbox: workflow.resetSandbox,
    runAssist: workflow.runAssist,
    submitProposal: workflow.submitProposal,
    restoreDraft: workflow.restoreDraft,
    syncEditorFromMarkdown,
  };
}
